/**
 * API client for HR admin team routes.
 *
 * Backend:
 *   GET    /api/v1/admin/teams?q=&limit=20         — case-insensitive name search
 *   GET    /api/v1/admin/teams/{team_id}/members   — full team roster
 *   POST   /api/v1/admin/teams                     — create (HR+ only)
 *   PATCH  /api/v1/admin/teams/{team_id}           — rename + thresholds
 *   DELETE /api/v1/admin/teams/{team_id}           — hard-delete (HR+ only)
 */
import type { Employee } from '@lms/shared-types'
import { apiRequest } from './client'

export interface TeamSearchResult {
  id: string
  name: string
  memberCount: number
}

export function searchTeams(q: string): Promise<TeamSearchResult[]> {
  const qs = new URLSearchParams({ q }).toString()
  return apiRequest<TeamSearchResult[]>(`/api/v1/admin/teams?${qs}`)
}

export function listTeamMembers(teamId: string): Promise<Employee[]> {
  return apiRequest<Employee[]>(`/api/v1/admin/teams/${teamId}/members`)
}

export interface AdminTeam {
  id: string
  name: string
}

/** Create a new team. HR+ only. Optionally place it under an org unit
 *  (`parentUnitId`) and/or set a manager (`managerId`). Returns the team. */
export function createTeam(
  name: string,
  parentUnitId?: string | null,
  managerId?: string | null,
): Promise<AdminTeam> {
  const body: { name: string; parentUnitId?: string | null; managerId?: string | null } = {
    name,
  }
  if (parentUnitId !== undefined) body.parentUnitId = parentUnitId
  if (managerId !== undefined) body.managerId = managerId
  return apiRequest<AdminTeam>('/api/v1/admin/teams', {
    method: 'POST',
    body,
  })
}

/** Assign / re-assign / unassign a team's org unit (structural only).
 *  Pass `null` to unassign. HR+ only. */
export function setTeamUnit(
  teamId: string,
  parentUnitId: string | null,
): Promise<AdminTeam> {
  return apiRequest<AdminTeam>(`/api/v1/admin/teams/${teamId}`, {
    method: 'PATCH',
    body: { parentUnitId },
  })
}

export interface UpdateTeamPayload {
  name?: string
  /** Team lead; null clears it. */
  managerId?: string | null
  /** Org unit; null unassigns. */
  parentUnitId?: string | null
}

/** Update a team's editable attributes in one PATCH (name / manager /
 *  org unit). Only supplied keys are changed. HR+ only. */
export function updateTeam(
  teamId: string,
  payload: UpdateTeamPayload,
): Promise<AdminTeam> {
  return apiRequest<AdminTeam>(`/api/v1/admin/teams/${teamId}`, {
    method: 'PATCH',
    body: payload,
  })
}

/** Rename a team (e.g. fix a typo). HR+ only. Partial PATCH — only the
 *  `name` field is sent. Refuses with 409 when another team already
 *  holds the name. Returns the updated team. */
export function renameTeam(teamId: string, name: string): Promise<AdminTeam> {
  return apiRequest<AdminTeam>(`/api/v1/admin/teams/${teamId}`, {
    method: 'PATCH',
    body: { name },
  })
}

/** Hard-delete a team. HR+ only. Refuses when the team has members or
 *  is referenced by any approval assignment. */
export function deleteTeam(teamId: string): Promise<void> {
  return apiRequest<null>(`/api/v1/admin/teams/${teamId}`, {
    method: 'DELETE',
  }).then(() => undefined)
}

